import {
    Color,
    Fog,
    AmbientLight,
    HemisphereLight,
    DirectionalLight,
    PMREMGenerator,
    Vector3,
    MathUtils,
} from 'three'
import { Sky } from 'three/examples/jsm/objects/Sky.js'
import Experience from '../Experience.js'

export default class Environment {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.renderer = this.experience.renderer.instance
        this.params = this.experience.config.environment
        this.debug = this.experience.debug
        this.defaultSkyConfig = this.params.sky ? { ...this.params.sky } : null
        this.defaultExposure = this.renderer.toneMappingExposure
        this.quality = this.experience.config.quality

        // Arc lumineux le long du parcours (voir applyProgress) : throttlé
        // car la recompilation de l'environnement PMREM est coûteuse.
        this.skyJourney = this.params.sky?.journey
        this.lightJourney = this.params.directionalLight?.journey
        this.journeyMinDelta = 0.003
        this.journeyMinIntervalMs = 220
        this.lastJourneyProgress = -1
        this.lastJourneyUpdate = 0

        // Brouillard réglé pour la caméra au ras du sol : il s'ouvre pour
        // les vues en altitude (descente d'ouverture, outro).
        this.baseFog = { near: this.params.scene.fogNear, far: this.params.scene.fogFar }
        this.fogOpening = -1

        if (this.params.scene.fogColorStart && this.params.scene.fogColorEnd) {
            this._fogStartColor = new Color(this.params.scene.fogColorStart)
            this._fogEndColor = new Color(this.params.scene.fogColorEnd)
        }

        if (this.lightJourney?.enabled) {
            this._journeyStartColor = new Color(this.lightJourney.colorStart)
            this._journeyEndColor = new Color(this.lightJourney.colorEnd)
        }

        // Debug
        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder({ title: 'Environment', expanded: false })
        }

        this.initScene()
        this.setSkyEnvironment()
        this.setSunLight()
        this.applyShadowQuality()
    }

    initScene() {
        const sceneParams = this.params.scene

        const fogColor = sceneParams.fogColorStart ?? sceneParams.backgroundColor
        this.scene.background = new Color(sceneParams.backgroundColor)
        this.scene.fog = new Fog(
            fogColor,
            sceneParams.fogNear,
            sceneParams.fogFar,
        )
        this.scene.environmentIntensity = sceneParams.environmentIntensity ?? 1

        if (this.debug.active) {
            const bgProxy = { backgroundColor: `#${this.scene.background.getHexString()}` };
            this.debugFolder.addBinding(bgProxy, 'backgroundColor').on('change', ev => {
                this.scene.background.set(ev.value);
                this.scene.fog.color.set(ev.value);
            });

            this.debugFolder.addBinding(sceneParams, 'fogNear', { min: 0, max: 10, step: 0.1 }).on('change', ev => {
                if (this.scene.fog) this.scene.fog.near = ev.value;
            });
            this.debugFolder.addBinding(sceneParams, 'fogFar', { min: 0, max: 20, step: 0.1 }).on('change', ev => {
                if (this.scene.fog) this.scene.fog.far = ev.value;
            });
        }
    }

    setSunLight() {
        this.ambientLight = new AmbientLight(
            this.params.ambientLight.color,
            this.params.ambientLight.intensity,
        );

        this.scene.add(this.ambientLight)

        const hemi = this.params.hemisphereLight
        if (hemi) {
            this.hemisphereLight = new HemisphereLight(hemi.skyColor, hemi.groundColor, hemi.intensity)
            this.scene.add(this.hemisphereLight)
        }

        this.dirLight = new DirectionalLight(
            this.params.directionalLight.color,
            this.params.directionalLight.intensity,
        )
        this.dirLight.position.set(
            this.params.directionalLight.position.x,
            this.params.directionalLight.position.y,
            this.params.directionalLight.position.z
        );

        this.scene.add(this.dirLight)
        this.scene.add(this.dirLight.target)

        if (this.debug.active) {

            this.debugFolder.addBinding(
                this.ambientLight, 'intensity',
                {
                    label: 'Light Intensity',
                    min: 0,
                    max: 5,
                    step: 0.01
                }
            );

            if (this.hemisphereLight) {
                this.debugFolder.addBinding(this.hemisphereLight, 'intensity', {
                    label: 'Hemisphere', min: 0, max: 5, step: 0.01
                })
            }

            this.debugFolder.addBinding(
                this.dirLight, 'intensity',
                {
                    label: 'Dirlight Intensity',
                    min: 0,
                    max: 5,
                    step: 0.01
                }
            );

            this.debugFolder.addBinding(
                this.dirLight.position, 'x',
                {
                    label: 'Dirlight X',
                    min: -15,
                    max: 15,
                    step: 0.01
                }
            );

            this.debugFolder.addBinding(
                this.dirLight.position, 'y',
                {
                    label: 'Dirlight Y',
                    min: 0,
                    max: 15,
                    step: 0.01
                }
            );

            this.debugFolder.addBinding(
                this.dirLight.position, 'z',
                {
                    label: 'Dirlight Z',
                    min: -15,
                    max: 15,
                    step: 0.01
                }
            );
        }
    }

    applyShadowQuality(presetName = this.quality?.preset ?? 'auto') {
        if (!this.dirLight) return

        let resolvedPreset = presetName
        if (presetName === 'auto') {
            resolvedPreset = this.experience.isMobile ? 'medium' : 'high'
        }

        const preset = this.quality?.presets?.[resolvedPreset]
        const targetShadowSize = preset?.shadowMapSize ?? 1536

        this.dirLight.shadow.mapSize.set(targetShadowSize, targetShadowSize)
        if (this.dirLight.shadow.map) {
            this.dirLight.shadow.map.dispose()
            this.dirLight.shadow.map = null
        }
    }

    setSkyEnvironment() {
        const skyConfig = this.params.sky
        if (!skyConfig?.enabled) return

        this.pmremGenerator = new PMREMGenerator(this.experience.renderer.instance)
        this.pmremGenerator.compileEquirectangularShader()

        this.sky = new Sky()
        this.sky.scale.setScalar(450000)
        this.setSkyHaze()
        this.scene.add(this.sky)

        this.sun = new Vector3()
        this.applySkyConfig()
        this.refreshEnvironmentMap()

        if (this.debug.active) {
            this.setSkyDebug()
        }
    }

    // Le Sky de three.js brûle l'horizon côté soleil et se voit à travers
    // les bords transparents du terrain. On le fond dans la couleur de
    // brume (même référence Color, donc synchrone avec le journey) et on
    // modère son gain pour garder un ciel dense, jamais blanc.
    // Un dégradé stylisé (zénith bleu -> horizon orangé) est ensuite mêlé
    // au ciel physique pour une palette plus gaie et plus lisible.
    setSkyHaze() {
        const haze = this.params.sky?.haze ?? {}
        const gradient = this.params.sky?.gradient ?? {}
        this.skyHazeUniforms = {
            hazeColor: { value: this.scene.fog?.color ?? new Color(this.params.scene.backgroundColor) },
            hazeAmount: { value: haze.amount ?? 0.85 },
            hazeHeight: { value: haze.height ?? 0.22 },
            skyGain: { value: haze.gain ?? 0.85 },
            zenithColor: { value: new Color(gradient.zenith ?? '#2f6fd1') },
            middleColor: { value: new Color(gradient.middle ?? '#8fc2f2') },
            horizonColor: { value: new Color(gradient.horizon ?? '#ffab6e') },
            gradientAmount: { value: gradient.amount ?? 0 },
            gradientIntensity: { value: gradient.intensity ?? 1 },
            sunGlowColor: { value: new Color(gradient.sunGlow ?? '#ffb066') },
            sunGlowStrength: { value: gradient.sunGlowStrength ?? 0 }
        }

        this.sky.material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, this.skyHazeUniforms)

            shader.fragmentShader = shader.fragmentShader
                .replace(
                    'void main() {',
                    `uniform vec3 hazeColor;
                    uniform float hazeAmount;
                    uniform float hazeHeight;
                    uniform float skyGain;
                    uniform vec3 zenithColor;
                    uniform vec3 middleColor;
                    uniform vec3 horizonColor;
                    uniform float gradientAmount;
                    uniform float gradientIntensity;
                    uniform vec3 sunGlowColor;
                    uniform float sunGlowStrength;
                    void main() {`
                )
                .replace(
                    'gl_FragColor = vec4( retColor, 1.0 );',
                    `vec3 viewDir = normalize( vWorldPosition - cameraPosition );
                    float hazeBand = 1.0 - smoothstep( -0.02, hazeHeight, viewDir.y );
                    retColor *= skyGain;

                    float elevation = clamp( viewDir.y, 0.0, 1.0 );
                    vec3 gradientColor = mix( horizonColor, middleColor, smoothstep( 0.0, 0.22, elevation ) );
                    gradientColor = mix( gradientColor, zenithColor, smoothstep( 0.18, 0.75, elevation ) );
                    retColor = mix( retColor, gradientColor * gradientIntensity, gradientAmount );

                    float sunAlign = max( dot( viewDir, vSunDirection ), 0.0 );
                    retColor += sunGlowColor * ( pow( sunAlign, 6.0 ) * 0.6 + pow( sunAlign, 48.0 ) ) * sunGlowStrength;

                    retColor = mix( retColor, hazeColor, hazeBand * hazeAmount );
                    gl_FragColor = vec4( retColor, 1.0 );`
                )
        }
    }

    applySkyConfig() {
        const skyConfig = this.params.sky
        const uniforms = this.sky.material.uniforms

        uniforms.turbidity.value = skyConfig.turbidity
        uniforms.rayleigh.value = skyConfig.rayleigh
        uniforms.mieCoefficient.value = skyConfig.mieCoefficient
        uniforms.mieDirectionalG.value = skyConfig.mieDirectionalG

        const theta = Math.PI * (skyConfig.inclination - 0.5)
        const phi = 2 * Math.PI * (skyConfig.azimuth - 0.5)
        this.sun.setFromSphericalCoords(1, theta, phi)
        uniforms.sunPosition.value.copy(this.sun)
    }

    refreshEnvironmentMap() {
        if (this.environmentRT) {
            this.environmentRT.dispose()
            this.environmentRT = null
        }

        const envRT = this.pmremGenerator.fromScene(this.sky)
        this.scene.environment = envRT.texture
        this.environmentRT = envRT
    }

    setSkyDebug() {
        const skyConfig = this.params.sky
        const skyFolder = this.debugFolder.addFolder({ title: 'Sky', expanded: false })
        this.skyFolder = skyFolder

        skyFolder.addBinding(skyConfig, 'turbidity', { min: 0, max: 20, step: 0.1 }).on('change', () => {
            this.applySkyConfig()
            this.refreshEnvironmentMap()
        })
        skyFolder.addBinding(skyConfig, 'rayleigh', { min: 0, max: 5, step: 0.01 }).on('change', () => {
            this.applySkyConfig()
            this.refreshEnvironmentMap()
        })
        skyFolder.addBinding(skyConfig, 'mieCoefficient', { min: 0.0001, max: 0.02, step: 0.0001 }).on('change', () => {
            this.applySkyConfig()
            this.refreshEnvironmentMap()
        })
        skyFolder.addBinding(skyConfig, 'mieDirectionalG', { min: 0, max: 1, step: 0.001 }).on('change', () => {
            this.applySkyConfig()
            this.refreshEnvironmentMap()
        })
        skyFolder.addBinding(skyConfig, 'inclination', { min: 0, max: 1, step: 0.001 }).on('change', () => {
            this.applySkyConfig()
            this.refreshEnvironmentMap()
        })
        skyFolder.addBinding(skyConfig, 'azimuth', { min: 0, max: 1, step: 0.001 }).on('change', () => {
            this.applySkyConfig()
            this.refreshEnvironmentMap()
        })

        const presetsFolder = skyFolder.addFolder({ title: 'Presets', expanded: false })

        presetsFolder.addButton({ title: 'Daylight' }).on('click', () => {
            this.applySkyPreset('daylight')
        })

        presetsFolder.addButton({ title: 'Golden Hour' }).on('click', () => {
            this.applySkyPreset('goldenHour')
        })

        presetsFolder.addButton({ title: 'Overcast' }).on('click', () => {
            this.applySkyPreset('overcast')
        })

        presetsFolder.addButton({ title: 'Reset Sky' }).on('click', () => {
            this.resetSky()
        })
    }

    applySkyPreset(presetName) {
        if (!this.params.sky) return

        const presets = {
            daylight: {
                turbidity: 8.5,
                rayleigh: 1.3,
                mieCoefficient: 0.005,
                mieDirectionalG: 0.88,
                inclination: 0.6,
                azimuth: 0.26,
                exposure: 0.9
            },
            goldenHour: {
                turbidity: 12,
                rayleigh: 1.0,
                mieCoefficient: 0.008,
                mieDirectionalG: 0.9,
                inclination: 0.54,
                azimuth: 0.23,
                exposure: 0.82
            },
            overcast: {
                turbidity: 16,
                rayleigh: 0.7,
                mieCoefficient: 0.012,
                mieDirectionalG: 0.8,
                inclination: 0.63,
                azimuth: 0.28,
                exposure: 0.75
            }
        }

        const preset = presets[presetName]
        if (!preset) return

        this.params.sky.turbidity = preset.turbidity
        this.params.sky.rayleigh = preset.rayleigh
        this.params.sky.mieCoefficient = preset.mieCoefficient
        this.params.sky.mieDirectionalG = preset.mieDirectionalG
        this.params.sky.inclination = preset.inclination
        this.params.sky.azimuth = preset.azimuth
        this.renderer.toneMappingExposure = preset.exposure

        this.applySkyConfig()
        this.refreshEnvironmentMap()
        this.skyFolder?.refresh?.()
        this.debugFolder?.refresh?.()
    }

    resetSky() {
        if (!this.defaultSkyConfig || !this.params.sky) return

        this.params.sky.turbidity = this.defaultSkyConfig.turbidity
        this.params.sky.rayleigh = this.defaultSkyConfig.rayleigh
        this.params.sky.mieCoefficient = this.defaultSkyConfig.mieCoefficient
        this.params.sky.mieDirectionalG = this.defaultSkyConfig.mieDirectionalG
        this.params.sky.inclination = this.defaultSkyConfig.inclination
        this.params.sky.azimuth = this.defaultSkyConfig.azimuth
        this.renderer.toneMappingExposure = this.defaultExposure

        this.applySkyConfig()
        this.refreshEnvironmentMap()
        this.skyFolder?.refresh?.()
        this.debugFolder?.refresh?.()
    }

    // 0 = brouillard normal, 1 = brouillard repoussé pour les vues aériennes
    setFogOpening(amount) {
        const fog = this.scene.fog
        if (!fog || Math.abs(amount - this.fogOpening) < 1e-3) return
        this.fogOpening = amount
        fog.near = MathUtils.lerp(this.baseFog.near, 6, amount)
        fog.far = MathUtils.lerp(this.baseFog.far, 40, amount)
    }

    // Fait doucement évoluer le ciel et la lumière directionnelle le
    // long du scroll (matinée claire -> fin de journée plus chaude).
    // Les réglages par défaut de config.js restent le point médian.
    applyProgress(progress) {
        if (!this.skyJourney?.enabled && !this.lightJourney?.enabled) return

        const now = performance.now()
        const delta = Math.abs(progress - this.lastJourneyProgress)
        if (delta < this.journeyMinDelta && now - this.lastJourneyUpdate < this.journeyMinIntervalMs) return

        this.lastJourneyProgress = progress
        this.lastJourneyUpdate = now

        const p = MathUtils.clamp(progress, 0, 1)

        if (this.skyJourney?.enabled && this.sky) {
            const { start, end } = this.skyJourney
            const skyConfig = this.params.sky

            skyConfig.turbidity = MathUtils.lerp(start.turbidity, end.turbidity, p)
            skyConfig.rayleigh = MathUtils.lerp(start.rayleigh, end.rayleigh, p)
            skyConfig.mieCoefficient = MathUtils.lerp(start.mieCoefficient, end.mieCoefficient, p)
            skyConfig.mieDirectionalG = MathUtils.lerp(start.mieDirectionalG, end.mieDirectionalG, p)
            skyConfig.inclination = MathUtils.lerp(start.inclination, end.inclination, p)
            skyConfig.azimuth = MathUtils.lerp(start.azimuth, end.azimuth, p)
            this.renderer.toneMappingExposure = MathUtils.lerp(start.exposure, end.exposure, p)

            this.applySkyConfig()

            // La convolution PMREM est coûteuse : contribution surtout
            // visible dans les reflets, on l'évite sur mobile.
            if (!this.experience.isMobile) this.refreshEnvironmentMap()
        }

        if (this.lightJourney?.enabled && this.dirLight) {
            this.dirLight.intensity = MathUtils.lerp(
                this.lightJourney.intensityStart,
                this.lightJourney.intensityEnd,
                p
            )
            this.dirLight.color.copy(this._journeyStartColor).lerp(this._journeyEndColor, p)
        }

        if (this._fogStartColor && this.scene.fog) {
            this.scene.fog.color.copy(this._fogStartColor).lerp(this._fogEndColor, p)
        }
    }

    destroy() {
        if (this.sky) {
            this.scene.remove(this.sky)
            this.sky.geometry?.dispose?.()
            this.sky.material?.dispose?.()
            this.sky = null
        }

        if (this.environmentRT) {
            this.environmentRT.dispose()
            this.environmentRT = null
        }

        if (this.pmremGenerator) {
            this.pmremGenerator.dispose()
            this.pmremGenerator = null
        }

        this.scene.environment = null

        if (this.dirLight?.shadow?.map) {
            this.dirLight.shadow.map.dispose();
            this.dirLight.shadow.map = null;
        }

        if (this.ambientLight) {
            this.scene.remove(this.ambientLight);
            this.ambientLight = null;
        }

        if (this.hemisphereLight) {
            this.scene.remove(this.hemisphereLight);
            this.hemisphereLight = null;
        }

        if (this.dirLight) {
            this.scene.remove(this.dirLight);
            this.dirLight.target = null;
            this.dirLight = null;
        }

        if (this.debug?.active && this.debugFolder) {
            this.debugFolder.dispose();
            this.debugFolder = null;
        }

        this.scene = null;
        this.renderer = null;
        this.params = null;
        this.debug = null;
        this.experience = null;
    }
}